// Voice Mode 2.0 — P1: the live-session WebSocket client.
//
// Thin, testable transport: it owns the socket, the wire protocol and the JWT
// handshake (subprotocol `saik.auth`, never a token in the URL). Audio frames
// arrive as binary int16 PCM; everything else is JSON.

// Spoken thoughts (2026-09-30): each labelled [[say]] utterance and its state.
export type SpokenState = 'pending' | 'speaking' | 'spoken' | 'skipped';

export interface SpokenSegment {
  index: number;
  text: string;
  hold: boolean;
  state: SpokenState;
  created_ms?: number;
  started_ms?: number | null;
  ended_ms?: number | null;
  skip_reason?: string | null;
}

export type VoiceServerEvent =
  | { type: 'ready'; session_id: string; sample_rate: number | null; format?: string; channels?: number; max_seconds?: number; silence_seconds?: number; endpointing_ms?: number }
  | { type: 'state'; state: 'listening' | 'thinking' | 'speaking' | 'ended' | string }
  | { type: 'sentence'; text: string; index?: number }
  | { type: 'first_audio'; latency_ms: number | null; sample_rate?: number | null; index?: number }
  | { type: 'usage'; usage: Record<string, number>; reasoning_chars?: number }
  | { type: 'interrupted'; source?: string; index?: number }
  | { type: 'turn_end'; index: number; sentences?: number; first_audio_latency_ms?: number | null; interrupted?: boolean; error?: string | null }
  | { type: 'error'; detail: string }
  | { type: 'ended'; reason: string }
  | { type: 'spoken_state'; current: number | null; segments: SpokenSegment[] }
  | { type: 'conversation_ack'; conversation_id: string | null }
  | { type: 'spoken'; action: SpokenState; index: number; text?: string; reason?: string | null; hold?: boolean };

export interface WebSocketLike {
  readyState: number;
  send(data: string | ArrayBufferLike): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: ((event: { code?: number; reason?: string }) => void) | null;
  onerror: ((event: unknown) => void) | null;
}

export type SocketFactory = (url: string, protocols: string[]) => WebSocketLike;

export interface VoiceLiveClientOptions {
  sessionId: string;
  token: string;
  /** Base origin for the socket, e.g. ``https://host`` or ``http://localhost:8000``. */
  origin?: string;
  socketFactory?: SocketFactory;
  onEvent?: (event: VoiceServerEvent) => void;
  onAudio?: (pcm: Int16Array) => void;
  onClose?: (reason: string) => void;
}

export const defaultSocketFactory: SocketFactory = (url, protocols) =>
  new WebSocket(url, protocols) as unknown as WebSocketLike;

/** ws(s)://host/api/v1/voice/session/{id}/ws — derived from the page origin. */
export function sessionSocketUrl(sessionId: string, origin?: string): string {
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const wsBase = base.replace(/^http/, 'ws').replace(/\/$/, '');
  return `${wsBase}/api/v1/voice/session/${sessionId}/ws`;
}

export class VoiceLiveClient {
  readonly sessionId: string;
  private readonly token: string;
  private readonly socketFactory: SocketFactory;
  private readonly origin?: string;
  private socket: WebSocketLike | null = null;
  private closedByUs = false;
  onEvent?: (event: VoiceServerEvent) => void;
  onAudio?: (pcm: Int16Array) => void;
  onClose?: (reason: string) => void;

  constructor(options: VoiceLiveClientOptions) {
    this.sessionId = options.sessionId;
    this.token = options.token;
    this.origin = options.origin;
    this.socketFactory = options.socketFactory ?? defaultSocketFactory;
    this.onEvent = options.onEvent;
    this.onAudio = options.onAudio;
    this.onClose = options.onClose;
  }

  connect(): void {
    const url = sessionSocketUrl(this.sessionId, this.origin);
    const socket = this.socketFactory(url, ['saik.auth', this.token]);
    this.socket = socket;

    socket.onmessage = (event) => {
      const data = event?.data;
      if (data instanceof ArrayBuffer) {
        if (this.onAudio) this.onAudio(bytesToInt16Array(data));
        return;
      }
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        void data.arrayBuffer().then((buf) => this.onAudio?.(bytesToInt16Array(buf)));
        return;
      }
      if (typeof data !== 'string') return;
      try {
        const payload = JSON.parse(data) as VoiceServerEvent;
        if (payload && typeof payload.type === 'string') this.onEvent?.(payload);
      } catch {
        /* ignore malformed frames rather than killing the session */
      }
    };

    socket.onclose = () => {
      this.socket = null;
      this.onClose?.(this.closedByUs ? 'closed' : 'disconnected');
    };
  }

  get connected(): boolean {
    // 1 === WebSocket.OPEN
    return !!this.socket && this.socket.readyState === 1;
  }

  sendUserText(text: string, conversationId?: string | null): void {
    // Always carry the currently-viewed chat on the turn itself so the server
    // re-binds per turn even if a separate ``conversation`` frame was dropped
    // while the socket was connecting (owner report 2026-10-02).
    this.sendJson({ type: 'user_text', text, conversation_id: conversationId ?? undefined });
  }

  setConversation(conversationId: string | null): void {
    // Re-points which chat this live session talks to; the server re-binds the
    // turn's brain per-turn, so switching chats mid-session takes effect now.
    try {
      this.sendJson({ type: 'conversation', conversation_id: conversationId });
    } catch {
      /* socket closed */
    }
  }

  bargeIn(): void {
    this.sendJson({ type: 'barge_in' });
  }

  heartbeat(): void {
    this.sendJson({ type: 'heartbeat' });
  }

  end(reason = 'user'): void {
    this.sendJson({ type: 'end', reason });
    this.close();
  }

  close(): void {
    this.closedByUs = true;
    const socket = this.socket;
    this.socket = null;
    try {
      socket?.close(1000);
    } catch {
      /* already gone */
    }
  }

  private sendJson(payload: Record<string, unknown>): void {
    if (!this.socket || this.socket.readyState !== 1) return;
    try {
      this.socket.send(JSON.stringify(payload));
    } catch {
      /* socket died between the check and the send */
    }
  }
}

function bytesToInt16Array(buffer: ArrayBuffer): Int16Array {
  const view = new Uint8Array(buffer);
  const usable = view.length - (view.length % 2);
  const aligned = new Uint8Array(usable);
  aligned.set(view.subarray(0, usable));
  return new Int16Array(aligned.buffer);
}
